// SPDX-FileCopyrightText: Copyright 2025 OP Financial Group (https://op.fi). All Rights Reserved.
//
// SPDX-License-Identifier: LicenseRef-OpAllRightsReserved

import {
    validateBusinessId,
    validateEmail,
    ValidateError,
    validateFinnishPostalCode,
    validateFinnishSSN,
    validatePhoneNumber,
} from '@op/opds.validate';
import { id, MAX_ADMINISTRATORS } from '../Constants';
import message from '../messages';
import { AdminData, AdminRolesData, CompanyData, ConfirmSignatory, ContractData } from '../context/ContextWrapper';
import { ErrorItem } from '../components/InputErrorTile';

const textRegex: RegExp = /^[a-zA-ZÅÄÖÉÁÈÀÑÜåäöéáèàüóòÒÓÇçñ0-9 .,'()\-/?!&]*$/;

export interface ValidationResult {
    message: string;
    notValid: boolean;
}

interface ValidationError {
    id: string;
    message: string;
    index?: number;
    isAdminError?: boolean;
}

const isValid: ValidationResult = { message: '', notValid: false };
const emptyValueError: ValidationResult = {
    message: message('emptyValueErrorMessage'),
    notValid: true,
};
const wrongValueError: ValidationResult = {
    message: message('wrongValueErrorMessage'),
    notValid: true,
};
const selectCompanyTypeError: ValidationResult = {
    message: message('selectCompanyTypeError'),
    notValid: true,
};

const acceptTermsValidationError: ValidationResult = {
    message: 'acceptTermsError',
    notValid: true,
};
const selectServiceTypeError: ValidationResult = {
    message: message('selectServiceTypeError'),
    notValid: true,
};

const validate = (value: string, checkTheValidation: boolean = true, notValid: boolean = false): ValidationResult => {
    if (!checkTheValidation) return isValid;
    if (value.trim() === '') return emptyValueError;
    if (notValid) return wrongValueError;
    return isValid;
};

export const isValidFinnishSSN = (value: string, checkTheValidation: boolean = true): ValidationResult =>
    validate(value, checkTheValidation, validateFinnishSSN(value) instanceof Error);
export const isValidTextInput = (value: string, checkTheValidation: boolean = true): ValidationResult =>
    validate(value, checkTheValidation, RegExp(textRegex).exec(value) === null);

export const isValidPhoneInput = (value: string, checkTheValidation: boolean = true): ValidationResult => {
    const phoneNumberValidation = validate(value, checkTheValidation, validatePhoneNumber(value) instanceof Error);

    //check if uses parentheses. Return error only if no other errors
    const valid = checkTheValidation || checkTheValidation === undefined;
    if (valid && !phoneNumberValidation?.notValid && value.match(/[()]/g)) {
        return {
            message: message('validationPhoneNumberParentheses'),
            notValid: true,
        };
    }
    return phoneNumberValidation;
};

export const isValidAdminSelectedServices = (
    value: Record<string, AdminRolesData>,
    checkValidation: boolean = true
): ValidationResult => {
    const adminsSelectedServices = Object.keys(value);
    if (!checkValidation) return isValid;
    return adminsSelectedServices.length === 0 ? selectServiceTypeError : isValid;
};

export const isValidEmailInput = (value: string, checkTheValidation: boolean = true): ValidationResult =>
    validate(value, checkTheValidation, !isValidEmail(value));

export const isValidBusinessId = (value: string, checkTheValidation: boolean = true): ValidationResult =>
    validate(value, checkTheValidation, validateBusinessId(value) instanceof ValidateError);

export const isValidPostalCodeInput = (value: string, checkValidation: boolean = true): ValidationResult =>
    validate(value, checkValidation, validateFinnishPostalCode(value) instanceof ValidateError);

export const isValidCompanyType = (
    value: string[],
    showAgreementTypes: boolean,
    checkValidation: boolean = true
): ValidationResult => {
    if (!checkValidation) return isValid;
    const isCompanyOwnInsuranceType = value.length === 1 && value.includes('company');
    const validationResult = !showAgreementTypes ? isCompanyOwnInsuranceType : value.length > 0;
    return validationResult ? isValid : selectCompanyTypeError;
};

export const isValidAcceptTermsInput = (checkBoxStatus: boolean, checkValidation: boolean = true): ValidationResult => {
    if (!checkValidation) return isValid;
    return checkBoxStatus ? isValid : acceptTermsValidationError;
};

export const isValidAdminCount = (administrators: AdminData[]): ValidationResult => {
    if (administrators.length > MAX_ADMINISTRATORS) {
        return { message: message('maxAdminReachedError'), notValid: true };
    }
    return isValid;
};

export const validateAdminInformationForm = ({
    firstName,
    lastName,
    personalIdentityCode,
    adminPhoneNumber,
    adminEmailAddress,
    agreementTypes,
}: AdminData) => {
    if (isValidTextInput(firstName).notValid) return false;
    if (isValidTextInput(lastName).notValid) return false;
    if (isValidFinnishSSN(personalIdentityCode).notValid) return false;
    if (isValidPhoneInput(adminPhoneNumber).notValid) return false;
    if (isValidAdminSelectedServices(agreementTypes).notValid) return false;

    return !isValidEmailInput(adminEmailAddress).notValid;
};

export const validateCompanyInformation = (
    {
        businessId,
        companyName,
        streetAddress,
        postalCode,
        cityTown,
        phone,
        companyTypes,
        isForeignCompany,
        country,
    }: CompanyData,
    isAdminFlow: boolean,
    showAgreementTypes: boolean
) => {
    const hasCountryValue = (country as string | undefined)?.trim() !== '';

    if (isValidBusinessId(businessId).notValid) return false;
    if (isValidTextInput(companyName).notValid) return false;
    if (isValidTextInput(streetAddress as string).notValid && !isAdminFlow) return false;
    if (isValidPostalCodeInput(postalCode as string).notValid && !isAdminFlow) return false;
    if (isValidTextInput(cityTown as string).notValid && !isAdminFlow) return false;
    if (isValidPhoneInput(phone as string).notValid && !isAdminFlow) return false;
    if (isForeignCompany && hasCountryValue && isValidTextInput(country as string).notValid && !isAdminFlow)
        return false;

    return !(isValidCompanyType(companyTypes, showAgreementTypes).notValid && !isAdminFlow);
};

export const validateFirstPage = (
    data: ContractData,
    updateInformation: any,
    isAdminFlow: boolean,
    showAgreementTypes: boolean
): boolean => {
    const contractData = { ...data };
    const administrators = contractData.administrators;
    let formValid = administrators?.every(validateAdminInformationForm) ?? false;
    if (isValidAdminCount(administrators).notValid) {
        formValid = false;
    }
    if (!validateCompanyInformation(contractData.companyData, isAdminFlow, showAgreementTypes)) {
        formValid = false;
    }
    if (!validateServiceHasAdminSelected(contractData.companyData.companyTypes, contractData.administrators)) {
        formValid = false;
    }
    contractData.checkValidation = !formValid;
    updateInformation(contractData);

    return formValid;
};

export const validateApprovalSignatory = ({ acceptTerms }: ConfirmSignatory): boolean => {
    return acceptTerms;
};

export const validatePreviewPage = (data: ContractData, updateInformation: any): boolean => {
    const contractData = { ...data };
    let formValid = true;
    if (!validateApprovalSignatory(contractData.confirmSignatory)) {
        formValid = false;
    }

    if (contractData.confirmSignatory.selectedSigning === 'tupas' && contractData.additionalSignatorySelection === '') {
        formValid = false;
    }

    if (
        contractData.confirmSignatory.selectedSigning === 'tupas' &&
        contractData.additionalSignatorySelection === 'yes'
    ) {
        contractData.additionalSignatories.forEach((signatory) => {
            if (
                isValidTextInput(signatory.firstName).notValid ||
                isValidTextInput(signatory.lastName).notValid ||
                isValidPhoneInput(signatory.phoneNumber).notValid ||
                isValidEmailInput(signatory.emailAddress).notValid
            ) {
                formValid = false;
            }
        });
    }

    //hotfix
    if (
        contractData.companyData.companyTypes.includes('company') &&
        contractData.administrators.some((admin: any) => admin.canEditRoles === undefined)
    ) {
        formValid = false;
    }

    contractData.checkValidation = !formValid;
    updateInformation(contractData);

    return formValid;
};

export const validateAdminFields = (admin: AdminData, errors: ValidationError[], index: number): void => {
    if (isValidTextInput(admin?.firstName).notValid) {
        errors.push({ id: id.FIRST_NAME, message: message('firstName'), index: index, isAdminError: true });
    }
    if (isValidTextInput(admin?.lastName).notValid) {
        errors.push({ id: id.LAST_NAME, message: message('lastName'), index: index, isAdminError: true });
    }
    if (isValidFinnishSSN(admin?.personalIdentityCode).notValid) {
        errors.push({
            id: id.PERSONAL_CODE,
            message: message('personalIdentityCode'),
            index: index,
            isAdminError: true,
        });
    }
    if (isValidPhoneInput(admin.adminPhoneNumber).notValid) {
        errors.push({ id: id.ADMIN_PHONE_NUMBER, message: message('phoneAdmin'), index: index, isAdminError: true });
    }
    if (isValidEmailInput(admin?.adminEmailAddress).notValid) {
        errors.push({ id: id.EMAIL, message: message('adminEmailAddress'), index: index, isAdminError: true });
    }
    if (isValidAdminSelectedServices(admin.agreementTypes).notValid) {
        errors.push({
            id: id.SERVICE_CHECKBOX,
            message: message('noServiceAddedForAdminMessage'),
            index: index,
            isAdminError: true,
        });
    }
};

export const isValidServiceHasAdminSelected = (selectedServices: string[], adminList: AdminData[]): string[] => {
    const servicesWithNoAdmin: string[] = [];
    selectedServices.forEach((service) => {
        const adminWithRights = adminList.filter((admin) =>
            Object.keys(admin.agreementTypes).find((selected) => selected === service)
        );
        if (adminWithRights.length === 0) {
            servicesWithNoAdmin.push(service);
        }
    });
    return servicesWithNoAdmin;
};

export const validateServiceHasAdminSelected = (selectedServices: string[], admins: AdminData[]): boolean => {
    const servicesWithNoAdmin = isValidServiceHasAdminSelected(selectedServices, admins);
    return servicesWithNoAdmin.length === 0;
};

export const getServiceHasNoAdminErrors = (contractData: ContractData): string[] => {
    const companyType = contractData.companyData.companyTypes;
    const agreementTypes = contractData.administrators;
    return isValidServiceHasAdminSelected(companyType, agreementTypes);
};

export const getFirstPageErrors = (
    contractData: ContractData,
    isAdminFlow: boolean,
    showAgreementTypes: boolean
): ValidationError[] => {
    const {
        businessId,
        companyName,
        streetAddress,
        postalCode,
        cityTown,
        phone,
        companyTypes,
        isForeignCompany,
        country,
    } = contractData.companyData;

    const errors: ValidationError[] = [];
    const hasCountryValue = (country as string | undefined)?.trim() !== '';

    if (isValidBusinessId(businessId).notValid) {
        errors.push({ id: id.BUSINESS_ID, message: message('businessId') });
    }
    if (isValidTextInput(companyName).notValid) {
        errors.push({ id: id.COMPANY_NAME, message: message('companyName') });
    }
    if (isValidTextInput(streetAddress as string).notValid && !isAdminFlow) {
        errors.push({ id: id.STREET_ADDRESS, message: message('streetAddress') });
    }
    if (isValidPostalCodeInput(postalCode as string).notValid && !isAdminFlow) {
        errors.push({ id: id.POSTAL_CODE, message: message('postalCode') });
    }
    if (isValidTextInput(cityTown as string).notValid && !isAdminFlow) {
        errors.push({ id: id.CITY_TOWN, message: message('cityTown') });
    }
    if (isValidPhoneInput(phone as string).notValid && !isAdminFlow) {
        errors.push({ id: id.PHONE, message: message('phone') });
    }
    if (isForeignCompany && hasCountryValue && isValidTextInput(country as string).notValid && !isAdminFlow) {
        errors.push({ id: id.COUNTRY, message: message('country') });
    }
    if (isValidCompanyType(companyTypes, showAgreementTypes).notValid && !isAdminFlow) {
        errors.push({ id: id.COMPANY_TYPE, message: message('companyType') });
        return errors;
    }
    if (isValidAdminCount(contractData.administrators).notValid) {
        errors.push({ id: id.ADMIN_MAX_COUNT, message: message('maxAdminReachedError') });
        return errors;
    }
    contractData.administrators?.forEach((admin, index) => {
        validateAdminFields(admin, errors, index);
    });

    return errors;
};

export const getPreviewPageErrors = (contractData: ContractData): ErrorItem[] => {
    const errors: ErrorItem[] = [];

    if (!contractData.confirmSignatory.acceptTerms) {
        errors.push({ id: id.APPROVE_TERMS, message: message('acceptTermsError') });
    }

    if (contractData.confirmSignatory.selectedSigning === 'tupas' && contractData.additionalSignatorySelection === '') {
        errors.push({ id: id.ADDITIONAL_SIGNATORY_SELECTION, message: message('additionalSignatorySelectionError') });
    }

    if (
        contractData.confirmSignatory.selectedSigning === 'tupas' &&
        contractData.additionalSignatorySelection === 'yes'
    ) {
        contractData.additionalSignatories.forEach((signatory, index) => {
            if (isValidTextInput(signatory.firstName).notValid) {
                errors.push({
                    id: id.ADDITIONAL_SIGNATORY_FIRST_NAME,
                    message: message('firstName'),
                    index,
                    isAdminError: true,
                });
            }
            if (isValidTextInput(signatory.lastName).notValid) {
                errors.push({
                    id: id.ADDITIONAL_SIGNATORY_LAST_NAME,
                    message: message('lastName'),
                    index,
                    isAdminError: true,
                });
            }
            if (isValidPhoneInput(signatory.phoneNumber).notValid) {
                errors.push({
                    id: id.ADDITIONAL_SIGNATORY_PHONE,
                    message: message('phone'),
                    index,
                    isAdminError: true,
                });
            }
            if (isValidEmailInput(signatory.emailAddress).notValid) {
                errors.push({
                    id: id.ADDITIONAL_SIGNATORY_EMAIL,
                    message: message('emailAddress'),
                    index,
                    isAdminError: true,
                });
            }
        });
    }

    return errors;
};

const isValidEmail = (value: string) => {
    // OPDS email validation is missing support for scandinavian characters and TLD length validation
    // This section adds additional checks:
    // 1. UTF8_SCANDS: Ensures the email does not contain Scandinavian characters (ÄäÖöÅå).
    // 2. TLD_LENGTH: Ensures the top-level domain (TLD) is at least two characters long.
    const UTF8_SCANDS = /[ÄäÖöÅå]/;
    const TLD_LENGTH = /\.[a-zA-Z]{2,}$/;
    if (UTF8_SCANDS.test(value) || !TLD_LENGTH.test(value)) {
        return false;
    }
    return !(validateEmail(value, { isTLDRequired: true }) instanceof Error);
};
